import type { DatasetRecord, Page } from '@deckspace/types';
import type { ApiClient } from '@deckspace/client';
import type { PageId, WorkspaceId } from '@deckspace/ids';

// ---------------------------------------------------------------------------
// Workspace list
// ---------------------------------------------------------------------------

export interface WorkspaceListItem {
  id: string;
  name: string | null;
  vizCount: number;
  datasetCount: number;
  pageCount: number;
  vizTypes: string[];
  updatedAt: string;
}

export async function fetchWorkspaces(api: ApiClient): Promise<WorkspaceListItem[]> {
  const items = await api.listWorkspacesWithSummary();
  return items.map((item) => ({
    id: item.id,
    name: item.name,
    vizCount: item.surfaceCount,
    datasetCount: item.datasetCount,
    pageCount: item.pageCount,
    vizTypes: item.vizTypes,
    updatedAt: item.createdAt,
  }));
}

// ---------------------------------------------------------------------------
// Workspace detail
// ---------------------------------------------------------------------------

export interface WorkspaceDetail {
  id: string;
  name: string | null;
  surfaces: import('@deckspace/types').Surface[];
  datasetRegistry: Map<string, DatasetRecord>;
  pages: Page[];
}

export async function fetchWorkspaceDetail(
  api: ApiClient,
  id: string
): Promise<WorkspaceDetail | null> {
  try {
    const detail = await api.getWorkspaceDetail(id as WorkspaceId);
    const datasetRegistry = new Map<string, DatasetRecord>();
    for (const ds of detail.datasets) {
      datasetRegistry.set(ds.name, ds);
    }
    return {
      id: detail.id,
      name: detail.name,
      surfaces: detail.surfaces,
      datasetRegistry,
      pages: detail.pages,
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Pages CRUD
// ---------------------------------------------------------------------------

export async function createPage(api: ApiClient, workspaceId: string, name: string): Promise<Page> {
  const result = await api.createPage(workspaceId as WorkspaceId, name);
  return {
    id: result.id,
    workspaceId: workspaceId as WorkspaceId,
    name: result.name,
    sortOrder: result.sortOrder,
    createdAt: new Date().toISOString(),
  };
}

export async function renamePage(api: ApiClient, pageId: string, newName: string): Promise<void> {
  await api.renamePage(pageId as PageId, newName);
}

export async function deletePage(api: ApiClient, pageId: string): Promise<boolean> {
  await api.deletePage(pageId as PageId);
  // Server returns 204 (no body) on success, throws on failure
  return true;
}

// ---------------------------------------------------------------------------
// Workspace CRUD
// ---------------------------------------------------------------------------

export async function createWorkspace(api: ApiClient, name: string): Promise<{ id: string }> {
  const result = await api.createWorkspace(name);
  // Create default page
  await api.createPage(result.id, 'Page 1');
  return { id: result.id };
}

export async function deleteWorkspace(api: ApiClient, id: string): Promise<void> {
  await api.deleteWorkspace(id as WorkspaceId);
}

export async function renameWorkspace(api: ApiClient, id: string, name: string): Promise<void> {
  await api.renameWorkspace(id as WorkspaceId, name);
}
